import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { HostingSalesPage } from "@/components/site/hosting-sales-page";
import { HostingQuoteDialog } from "@/components/site/hosting-quote-dialog";
import { getHostingPageMeta } from "@/lib/hosting-public.functions";
import { DomainSearch } from "@/components/sections/domain-search";
import { AiDomainResearch } from "@/components/sections/ai-domain-research";

const SLUG = "hosting/domains";
const CANONICAL = "/hosting/domains";
const FALLBACK_TITLE = "Domains — 500+ TLDs with Free Privacy & DNS";
const FALLBACK_DESC =
  "Register .com, .ai, .io and 500+ TLDs with free WHOIS privacy, anycast DNS, email forwarding, DNSSEC and one-click SSL.";

export const Route = createFileRoute("/hosting/domains/")({
  component: DomainsIndex,
  loader: () => getHostingPageMeta({ data: { slug: SLUG } }).catch(() => null),
  head: ({ loaderData }) => {
    const meta = loaderData ?? null;
    const title = meta?.seo_title || meta?.title || FALLBACK_TITLE;
    const desc = meta?.seo_description || meta?.subtitle || FALLBACK_DESC;
    const image = meta?.og_image_url || meta?.hero_image_url || undefined;
    const tags: Array<Record<string, string>> = [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: desc },
    ];
    if (image) {
      tags.push({ property: "og:image", content: image });
      tags.push({ name: "twitter:image", content: image });
    }
    return { meta: tags, links: [{ rel: "canonical", href: CANONICAL }] };
  },
});

function DomainsIndex() {
  const [pickedQuery, setPickedQuery] = useState<string | undefined>(undefined);

  const handlePick = (q: string) => {
    setPickedQuery(q);
    if (typeof document !== "undefined") {
      document.getElementById("domain-search")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const before = (
    <>
      {/* Live availability search */}
      <section id="domain-search" className="border-y border-border bg-gradient-to-b from-muted/40 to-background">
        <div className="mx-auto max-w-5xl px-4 py-10 sm:py-14 lg:px-8">
          <div className="mb-6 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">Find your domain</p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">
              Search 500+ TLDs in one shot
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Type your idea — we'll check availability and premium alternatives instantly.
            </p>
          </div>
          <DomainSearch embedded initialQuery={pickedQuery} autoSearch />
        </div>
      </section>

      {/* AI-powered name research */}
      <section id="ai-research" className="border-b border-border bg-background">
        <AiDomainResearch onPick={handlePick} />
      </section>
    </>
  );

  const after = (
    <section id="quote" className="border-y border-border bg-gradient-to-b from-background to-muted/30 py-14 sm:py-16">
      <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 px-4 text-center lg:px-8">
        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Need bulk or brokerage?</h2>
        <p className="text-sm text-muted-foreground sm:text-base">
          Portfolios of 50+ domains, corporate transfers, or a specific premium name — our team
          handles negotiation, escrow and legal transfer end-to-end.
        </p>
        <HostingQuoteDialog
          slug={SLUG}
          title="Request domain brokerage"
          description="Tell us which domain you want or how many you need to manage — we'll respond within one business day."
        />
      </div>
    </section>
  );

  return <HostingSalesPage slug={SLUG} beforeContent={before} afterContent={after} />;
}

